package nl.volleyplanner.api.sports

import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import nl.volleyplanner.api.planner.Task
import nl.volleyplanner.api.types.PublicDataPayload
import nl.volleyplanner.extensions.durationUntil
import nl.volleyplanner.extensions.overlaps
import kotlin.time.ExperimentalTime

@Serializable
enum class MatchStatus {
    Planned,
    Completed,
    Cancelled
}

enum class TeamIdentifier {
    Home,
    Guest;

    fun isHome() : Boolean = this == Home
    fun isGuest(): Boolean = this == Guest

    operator fun not() : TeamIdentifier {
        return when(this) {
            Home -> Guest
            Guest -> Home
        }
    }
}

enum class TeamMatchResult {
    Win,
    Tie,
    Lose
}

@Serializable
data class SetResult(
    val set: Int,
    val homePoints: Int,
    val awayPoints: Int
) {
    fun getTeamResult(team: TeamIdentifier) : TeamMatchResult {
        val teamPoints = getPoints(team)
        val oppossingPoints = getPoints(!team)
        return when {
            teamPoints > oppossingPoints -> TeamMatchResult.Win
            teamPoints < oppossingPoints -> TeamMatchResult.Lose
            teamPoints == oppossingPoints -> TeamMatchResult.Tie
            else -> throw IllegalStateException("Impossible scenario")
        }
    }

    fun getPoints(team: TeamIdentifier) : Int  {
        return when(team) {
            TeamIdentifier.Home -> homePoints
            TeamIdentifier.Guest -> awayPoints
        }
    }
}

@Serializable
data class MatchEvent(
    val match: Match,
    val gameRuleset: String?,   // Keep here or move to Match? We won't need it in many other instances the Match is used
    val usePlanner: Boolean,
    val refereeTasks: List<Task>,
    val scoreKeeperTasks: List<Task>
) : PublicDataPayload {
    @Suppress("TRANSIENT_IS_REDUNDANT")
    @Transient
    val allTasks: List<Task>
        get() = refereeTasks + scoreKeeperTasks

}

@Serializable
data class Match(
    val id: String,
    val competitionType: CompetitionType,
    val pouleId: String,
    val matchStatus: MatchStatus,
    val teamHome: String,
    val teamGuest: String,
    val startDateTime: LocalDateTime?,
    val endDateTime: LocalDateTime?,
    val accommodation: String?,
    val field: String?,
    val setResults: List<SetResult>?,
    val matchStatusReason: String?
) : PublicDataPayload {
    @Transient
    val setsA = setResults?.let { it.count { it.homePoints > it.awayPoints } + it.count { it.homePoints == it.awayPoints } * 0.5 }
    @Transient
    val setsB = setResults?.let { it.count { it.homePoints < it.awayPoints } + it.count { it.homePoints == it.awayPoints } * 0.5 }

    @Transient
    val pointsA = setResults?.sumOf { it.homePoints }
    @Transient
    val pointsB = setResults?.sumOf { it.awayPoints }

    fun hasScore() : Boolean = setResults != null

    fun hasTeam(teamName: String) = teamHome == teamName || teamGuest == teamName

    fun identifyTeam(teamName: String) : TeamIdentifier {
        return when {
            teamName == teamHome -> TeamIdentifier.Home
            teamName == teamGuest -> TeamIdentifier.Guest
            else -> throw IllegalArgumentException("Team $teamName does not play in Match ($teamHome vs $teamGuest)")
        }
    }

    fun getTeamName(team: TeamIdentifier) : String {
        return when (team) {
            TeamIdentifier.Home -> teamHome
            TeamIdentifier.Guest -> teamGuest
        }
    }

    fun getTeamResult(team: TeamIdentifier) : TeamMatchResult {
        val teamSets = getSets(team)
        val opposingSets = getSets(!team)
        return when {
            teamSets == null || opposingSets == null -> throw IllegalStateException("No score for match available")
            teamSets > opposingSets -> TeamMatchResult.Win
            teamSets < opposingSets -> TeamMatchResult.Lose
            teamSets == opposingSets -> TeamMatchResult.Tie
            else -> throw IllegalStateException("Impossible scenario")
        }
    }

    fun getSets(team: TeamIdentifier) : Double? {
        return when (team) {
            TeamIdentifier.Home -> setsA
            TeamIdentifier.Guest -> setsB
        }
    }

    fun getPoints(team: TeamIdentifier) : Int? {
        return when (team) {
            TeamIdentifier.Home -> pointsA
            TeamIdentifier.Guest -> pointsB
        }
    }

    @OptIn(ExperimentalTime::class)
    @Transient
    val dateRange = startDateTime?.let { it.rangeTo(endDateTime!!) }

    fun overlaps(other: Match) = dateRange?.overlaps(other.dateRange)

    fun durationTo(other: Match) = dateRange?.durationUntil(other.dateRange)
}
